import {
    CellClassParams,
    ColDef,
    ColGroupDef,
    IAggFuncParams,
    IRowNode,
    ITooltipParams,
    ValueFormatterParams,
    ValueGetterParams
} from 'ag-grid-community';
import { percentageFormatter } from 'src/util/grid/cellFormatter';

import { formatMonthHeader } from '../../utils';

// Utility function to identify UMG & affiliate vendors
export const isUmgAffiliate = (vendor: string): boolean => {
    if (!vendor) return false;
    const vendorLower = vendor.toLowerCase();
    return vendorLower.includes('universal') && vendorLower.includes('affiliate');
};

// Common value formatter for number columns
export const numberFormatter = (params: ValueFormatterParams) => {
    if (params.value === null || params.value === '') return '';
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(params.value);
};

// Util function to create valueGetter for different column types
export const createValueGetter = (
    month: string,
    mode: 'actm' | 'standard',
    field: 'VendorPlays' | 'TotalPlays' | 'Mss',
    isAntiDilutionMode: boolean
) => {
    return (params: ValueGetterParams) => {
        // Use music-only data for UMG vendors when anti-dilution mode is ON
        if (isAntiDilutionMode && isUmgAffiliate(params.data?.vendor)) {
            const musicOnlyValue = params.data?.[`${month}_${mode}MusicOnly${field}`];
            return musicOnlyValue != null ? musicOnlyValue : 0;
        }
        // Use standard data for all other cases
        const value = params.data?.[`${month}_${mode}${field}`];
        return value != null ? value : 0;
    };
};

export const isEligibleForHighlighting = (
    params: CellClassParams | ITooltipParams,
    month: string,
    eligibleMode: 'actm' | 'standard'
) => {
    if (params.node?.group) {
        const nonEligibleCount =
            params.node.allLeafChildren?.filter((child) => child.data?.[`${month}_eligibleMode`] !== eligibleMode)
                .length || 0;

        return nonEligibleCount <= (params.node.allLeafChildren?.length || 0) / 2;
    }
    return params.data?.[`${month}_eligibleMode`] === eligibleMode;
};

// Util function to create cellClassRules for eligibility highlighting
export const createEligibilityCellClassRules = (month: string, eligibleMode: 'actm' | 'standard') => {
    return {
        'eligible-submission': (params: CellClassParams) => isEligibleForHighlighting(params, month, eligibleMode)
    };
};

// Util function to create MSS aggregation function
export const createMssAggFunc = (month: string, mode: 'actm' | 'standard', isAntiDilutionMode: boolean) => {
    return (params: IAggFuncParams) => {
        if (!params.values || params.values.length === 0) {
            return 0;
        }
        let aggVendorPlays = 0;
        let aggTotalPlays = 0;

        params.rowNode.allLeafChildren?.forEach((childNode) => {
            const rowData = childNode.data;
            if (rowData) {
                let vendorPlays = 0;
                let totalPlays = 0;

                if (isAntiDilutionMode && isUmgAffiliate(rowData.vendor)) {
                    vendorPlays = Number(rowData[`${month}_${mode}MusicOnlyVendorPlays`]) || 0;
                    totalPlays = Number(rowData[`${month}_${mode}MusicOnlyTotalPlays`]) || 0;
                } else {
                    vendorPlays = Number(rowData[`${month}_${mode}VendorPlays`]) || 0;
                    totalPlays = Number(rowData[`${month}_${mode}TotalPlays`]) || 0;
                }

                aggVendorPlays += vendorPlays;
                aggTotalPlays += totalPlays;
            }
        });

        return aggTotalPlays > 0 ? aggVendorPlays / aggTotalPlays : 0;
    };
};

// Util function to create column definition for a specific metric
const createMetricColumn = (
    month: string,
    mode: 'actm' | 'standard',
    field: 'VendorPlays' | 'TotalPlays' | 'Mss',
    headerName: string,
    minWidth: number,
    isAntiDilutionMode: boolean
): ColDef => {
    const isMssColumn = field === 'Mss';
    const isActmColumn = mode === 'actm';

    return {
        field: `${month}_${mode.toLowerCase()}${field}`,
        headerName,
        filter: 'agNumberColumnFilter',
        flex: 1,
        minWidth,
        sortable: true,
        valueFormatter: isMssColumn ? percentageFormatter : numberFormatter,
        valueGetter: createValueGetter(month, mode, field, isAntiDilutionMode),
        cellClassRules: createEligibilityCellClassRules(month, mode),
        aggFunc: isMssColumn ? createMssAggFunc(month, mode, isAntiDilutionMode) : 'sum',
        headerTooltip: isActmColumn ? 'ACTM: Artist Centric Threshold Model' : '',
        tooltipValueGetter: (params: ITooltipParams) => {
            return isEligibleForHighlighting(params, month, mode) ? 'Actuals' : '';
        }
    };
};

// Util function to create simple column definition for Prime/AMF services (no mode prefix)
const createSimpleMetricColumn = (
    month: string,
    field: 'vendorPlays' | 'totalPlays' | 'mss',
    headerName: string,
    minWidth: number
): ColDef => {
    const isMssColumn = field === 'mss';

    return {
        field: `${month}_${field}`,
        headerName,
        filter: 'agNumberColumnFilter',
        flex: 1,
        minWidth,
        sortable: true,
        valueFormatter: isMssColumn ? percentageFormatter : numberFormatter,
        valueGetter: (params: ValueGetterParams) => {
            const value = params.data?.[`${month}_${field}`];
            return value != null ? value : 0;
        },
        aggFunc: isMssColumn ? createSimpleMssAggFunc(month) : 'sum'
    };
};

// Util function to create MSS aggregation function for simple data structure
const createSimpleMssAggFunc = (month: string) => {
    return (params: IAggFuncParams) => {
        if (!params.values || params.values.length === 0) {
            return 0;
        }
        let aggVendorPlays = 0;
        let aggTotalPlays = 0;

        params.rowNode.allLeafChildren?.forEach((childNode: IRowNode) => {
            const rowData = childNode.data;
            if (rowData) {
                const vendorPlays = Number(rowData[`${month}_vendorPlays`]) || 0;
                const totalPlays = Number(rowData[`${month}_totalPlays`]) || 0;

                aggVendorPlays += vendorPlays;
                aggTotalPlays += totalPlays;
            }
        });

        return aggTotalPlays > 0 ? aggVendorPlays / aggTotalPlays : 0;
    };
};

/**
 * Creates dynamic column definitions for MSS pivot view
 * Territory, Vendor, then dynamic month column groups with 6 sub-columns each (ACTM + Standard)
 * @param availableMonths - Array of month strings (YYYYMM format)
 * @param isAntiDilutionMode - Whether to show music-only data for UMG vendors
 * @param serviceId - Service ID to determine column structure (1=AMU shows both ACTM+Standard, others show Standard only)
 */
export const createMssPivotColDefs = (
    availableMonths: string[],
    isAntiDilutionMode = false,
    serviceId = 1
): (ColDef | ColGroupDef)[] => {
    const baseColumns: ColDef[] = [
        {
            field: 'vendor',
            headerName: 'Vendor',
            flex: 2,
            minWidth: 180,
            sortable: true,
            rowGroup: true,
            hide: true
        }
    ];

    // Create dynamic month column groups based on service type
    const monthColumnGroups: ColGroupDef[] =
        serviceId === 1
            ? availableMonths.map((month) => ({
                  headerName: formatMonthHeader(month),
                  children: [
                      // ACTM Columns (isArtistCentric=true)
                      createMetricColumn(month, 'actm', 'VendorPlays', 'ACTM Vendor Plays', 130, isAntiDilutionMode),
                      createMetricColumn(month, 'actm', 'TotalPlays', 'ACTM Total Plays', 130, isAntiDilutionMode),
                      createMetricColumn(month, 'actm', 'Mss', 'ACTM MSS %', 110, isAntiDilutionMode),
                      // Standard Columns (isArtistCentric=false)
                      createMetricColumn(
                          month,
                          'standard',
                          'VendorPlays',
                          'Standard Vendor Plays',
                          140,
                          isAntiDilutionMode
                      ),
                      createMetricColumn(
                          month,
                          'standard',
                          'TotalPlays',
                          'Standard Total Plays',
                          140,
                          isAntiDilutionMode
                      ),
                      createMetricColumn(month, 'standard', 'Mss', 'Standard MSS %', 120, isAntiDilutionMode)
                  ]
              }))
            : // Prime/AMF services: 3 columns per month (simple structure)
              availableMonths.map((month) => ({
                  headerName: formatMonthHeader(month),
                  children: [
                      createSimpleMetricColumn(month, 'vendorPlays', 'Vendor Plays', 110),
                      createSimpleMetricColumn(month, 'totalPlays', 'Total Plays', 110),
                      createSimpleMetricColumn(month, 'mss', 'MSS %', 100)
                  ]
              }));

    return [...baseColumns, ...monthColumnGroups];
};

/**
 * Static column definitions for when no data is available
 */
export const mssPivotColDefsEmpty: (ColDef | ColGroupDef)[] = [
    {
        field: 'territory',
        headerName: 'Territory',
        filter: 'agSetColumnFilter',
        flex: 1,
        minWidth: 120,
        sortable: true
    },
    {
        field: 'vendor',
        headerName: 'Vendor',
        filter: 'agSetColumnFilter',
        flex: 2,
        minWidth: 180,
        sortable: true
    }
];
